import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef } from 'react';

import { FileDropZone } from '@/components/blocks/FileDropZone';
import { YStack } from '@/components/ui';
import { useThreadVisibility } from '@/features/chat/use-thread-visibility';
import { HivemindBanner } from '@/features/hivemind';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { classList } from '@/utils/cn';
import { COMPOSER_MIN_HEIGHT } from '@/utils/constants';

import { AgentComposer } from './agents-composer';
import { AgentMessageList } from './agents-message-list';

type AgentThreadProps = {
  threadId: string;
};

export const AgentThread = observer(({ threadId }: AgentThreadProps) => {
  const { threads: threadsStore } = useStores();
  const isDraggingFile = uiStore.isDraggingFile;
  const chatWidth = uiStore.chatWidth;
  const [threadContainerRef, threadRect] = useResizeObserver<HTMLDivElement>();
  const [messageBoxRef, messageBoxRect] = useResizeObserver<HTMLDivElement>();
  const messageListHeight = useMemo(
    () =>
      Math.max(threadRect.height - messageBoxRect.height, COMPOSER_MIN_HEIGHT),
    [messageBoxRect.height, threadRect.height]
  );

  const sendAskUserAnswersRef = useRef<() => void>(() => {});
  useThreadVisibility(threadId);

  useEffect(() => {
    void threadsStore.openThread(threadId);
  }, [threadId, threadsStore]);

  return (
    <>
      <FileDropZone show={isDraggingFile} />
      <YStack
        className={cn('relative min-h-0 min-w-0 flex-1 select-text', {
          'opacity-10': isDraggingFile,
        })}
        ref={threadContainerRef}
      >
        <YStack className="min-h-0 flex-1 overflow-hidden">
          <AgentMessageList
            threadId={threadId}
            height={messageListHeight}
            onAskUserComplete={() => sendAskUserAnswersRef.current()}
          />
        </YStack>
        <YStack
          ref={messageBoxRef}
          className={classList(
            'w-full self-center',
            'text-foreground',
            'pb-3 md:pb-6 px-3 md:px-6',
            chatWidth
          )}
        >
          <div className="pb-2 empty:hidden">
            <HivemindBanner threadId={threadId} inPanel />
          </div>
          <AgentComposer
            threadId={threadId}
            sendAskUserAnswersRef={sendAskUserAnswersRef}
            setIsDraggingFile={(value) => uiStore.setIsDraggingFile(value)}
          />
        </YStack>
      </YStack>
    </>
  );
});
